import { DateTime } from 'luxon';
import { SERVER_TIMEZONE } from '../../Components/TzDate/utils';

export const REPORT_TYPE_IDS = {
  clicksByMerchant: 1,
  clicksByReferrer: 2,
  clicksSummary: 3,
  merchantsOfferSummary: 4,
  actionLogsByUser: 5,
} as const;

export const REPORT_QUERY_STALE_TIME_MS = 5 * 60 * 1000;

export interface ReportFilter {
  region_ids?: (string | number)[];
  granularity?: ReportGranularity;
  greaterOrEqual?: {
    date?: string;
  };
  lessOrEqual?: {
    date?: string;
  };
}

export type ReportGranularity = 'daily' | 'hourly';

export const reportDataTableSx = {
  '& .RaDataTable-tbody .MuiTableCell-root': {
    backgroundColor: 'background.paper',
  },
};

export function formatDateString(dt: DateTime): string {
  return dt.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS");
}

export const PERIOD_CHOICES = [
  { id: 'today', name: 'Today' },
  { id: 'yesterday', name: 'Yesterday' },
  { id: 'this_week', name: 'This Week' },
  { id: 'last_week', name: 'Last Week' },
  { id: 'last_7_days', name: 'Last 7 days' },
  { id: 'last_14_days', name: 'Last 14 days' },
  { id: 'last_30_days', name: 'Last 30 days' },
  { id: 'this_month', name: 'This Month' },
  { id: 'last_month', name: 'Last Month' },
  { id: 'last_3_months', name: 'Last 3 months' },
  { id: 'last_6_months', name: 'Last 6 months' },
  { id: 'last_12_months', name: 'Last 12 months' },
];

export function getPeriodDateRange(period?: string): { from: string; to: string } | undefined {
  const today = DateTime.now().setZone(SERVER_TIMEZONE).startOf('day');
  const thisWeekStart = today.startOf('week');

  switch (period) {
    case 'today':
      return { from: formatDateString(today), to: formatDateString(today) };
    case 'yesterday': {
      const yesterday = today.minus({ days: 1 });
      return { from: formatDateString(yesterday), to: formatDateString(yesterday) };
    }
    case 'this_week':
      return {
        from: formatDateString(thisWeekStart),
        to: formatDateString(thisWeekStart.plus({ days: 6 })),
      };
    case 'last_week':
      return {
        from: formatDateString(thisWeekStart.minus({ days: 7 })),
        to: formatDateString(thisWeekStart.minus({ days: 1 })),
      };
    case 'last_7_days':
      return { from: formatDateString(today.minus({ days: 7 })), to: formatDateString(today) };
    case 'last_14_days':
      return { from: formatDateString(today.minus({ days: 14 })), to: formatDateString(today) };
    case 'last_30_days':
      return { from: formatDateString(today.minus({ days: 30 })), to: formatDateString(today) };
    case 'this_month':
      return {
        from: formatDateString(today.startOf('month')),
        to: formatDateString(today.endOf('month').startOf('day')),
      };
    case 'last_month': {
      const lastMonth = today.minus({ months: 1 });
      return {
        from: formatDateString(lastMonth.startOf('month')),
        to: formatDateString(lastMonth.endOf('month').startOf('day')),
      };
    }
    case 'last_3_months':
      return { from: formatDateString(today.minus({ months: 3 })), to: formatDateString(today) };
    case 'last_6_months':
      return { from: formatDateString(today.minus({ months: 6 })), to: formatDateString(today) };
    case 'last_12_months':
      return { from: formatDateString(today.minus({ months: 12 })), to: formatDateString(today) };
    default:
      return undefined;
  }
}

export const startOfMonth = formatDateString(
  DateTime.now().setZone(SERVER_TIMEZONE).startOf('month').startOf('day'),
);

export const today = formatDateString(DateTime.now().setZone(SERVER_TIMEZONE).startOf('day'));

export function escapeCsv(value: unknown): string {
  const s = value != null ? String(value) : '';
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function getReportDateLabels(filter: ReportFilter): { startDate: string; endDate: string } {
  const from = filter.greaterOrEqual?.date ?? '';
  const to = filter.lessOrEqual?.date ?? '';
  const end = DateTime.fromISO(to, { zone: SERVER_TIMEZONE });
  return {
    startDate: from.slice(0, 10),
    endDate: end.isValid ? end.toFormat('yyyy-LL-dd') : to.slice(0, 10),
  };
}
